import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useUserRole } from "@/hooks/useUserRole";
import {
  getMissingConfirmDocs,
  formatMissingDocs,
  hasLoadStatusMismatch,
  type ConfirmDocCustomer,
  type MissingConfirmDoc,
} from "@/lib/confirm-doc-gating";


interface ConfirmDocGateArgs {
  orderId?: string | null;
  billingPartyId?: string | null;
  tenderId?: string | null;
  /** Already-loaded billing party, when the caller has it (skips a fetch). */
  customer?: ConfirmDocCustomer | null;
  /** In-flight "Pending BOL" value from the form, when the caller has one. */
  pendingBol?: boolean;
}

/**
 * Fetches everything the confirm-time documentation gate needs for a single
 * order. Shared by the React hook and the mutation pre-flight so the UI and
 * the write path can never disagree.
 *
 * Documents are order-scoped (BOL/Tender images and `tender_id` live on the
 * parent), so multi-leg orders are evaluated at the parent level.
 */
export async function fetchMissingConfirmDocs(args: {
  orderId?: string | null;
  billingPartyId?: string | null;
  tenderId?: string | null;
  tenantId?: string | null;
  customer?: ConfirmDocCustomer | null;
  /** In-flight form value for `orders.pending_bol` (create / edit). */
  pendingBol?: boolean;
  /** Physical trailer load statuses when the caller already resolved them. */
  legTrailerLoadStatuses?: (string | null)[];
}): Promise<{
  missing: MissingConfirmDoc[];
  customerName: string | null;
  /** True when at least one leg's trailer is physically `empty`. */
  pendingBolEligible: boolean;
  /** Persisted `orders.pending_bol`, as stored (before eligibility re-check). */
  pendingBolFlag: boolean;
}> {
  const { orderId, billingPartyId, tenderId, tenantId } = args;
  let customer = args.customer ?? null;

  if (!customer && billingPartyId) {
    const { data, error } = await supabase
      .from("customers")
      .select("organization_name, requires_tender_id, requires_bol, requires_tender")
      .eq("id", billingPartyId)
      .maybeSingle();
    if (error) throw error;
    customer = (data as ConfirmDocCustomer | null) ?? null;
  }
  // API-created orders (those with an `external_order_id`) additionally have
  // their planned load status cross-checked against the physical trailer.
  // That check is independent of the billing party's document requirements.
  let isApiOrder = false;
  let pendingBolFlag = args.pendingBol === true;
  if (orderId) {
    let q = supabase
      .from("orders")
      .select("external_order_id, pending_bol")
      .eq("id", orderId);
    if (tenantId) q = q.eq("tenant_id", tenantId);
    const { data, error } = await q.maybeSingle();
    if (error) throw error;
    const row = data as { external_order_id: string | null; pending_bol: boolean | null } | null;
    isApiOrder = !!row?.external_order_id;
    // An explicit in-flight form value (create/edit) wins over the stored one.
    pendingBolFlag = args.pendingBol ?? row?.pending_bol === true;
  }

  if (!customer && !isApiOrder) {
    return {
      missing: [],
      customerName: null,
      pendingBolEligible: false,
      pendingBolFlag,
    };
  }

  // Only pay for the image lookups the customer actually requires.
  const needsBol = !!customer?.requires_bol;
  const needsTender = !!customer?.requires_tender;
  let hasBolImage = true;
  let hasTenderImage = true;

  if (orderId && (needsBol || needsTender)) {
    const scoped = (table: "order_bol_images" | "order_tender_images") => {
      let q = supabase.from(table).select("id").eq("order_id", orderId).limit(1);
      if (tenantId) q = q.eq("tenant_id", tenantId);
      return q;
    };
    if (needsBol) {
      const { data, error } = await scoped("order_bol_images");
      if (error) throw error;
      hasBolImage = (data?.length ?? 0) > 0;
    }
    if (needsTender) {
      const { data, error } = await scoped("order_tender_images");
      if (error) throw error;
      hasTenderImage = (data?.length ?? 0) > 0;
    }
  } else if (!orderId) {
    // Brand-new order: no images can exist yet.
    hasBolImage = !needsBol ? true : false;
    hasTenderImage = !needsTender ? true : false;
  }

  // Leg → trailer resolution is shared by the empty-trailer BOL waiver and the
  // API load-status cross-check, so both always read the same source.
  const needsLegTrailers =
    !!orderId && ((needsBol && !hasBolImage) || isApiOrder || pendingBolFlag);
  const legTrailers = needsLegTrailers
    ? await resolveLegTrailers(orderId as string, tenantId ?? null)
    : [];

  const allLegTrailersEmpty =
    needsBol && !hasBolImage && legTrailers.length > 0
      ? areAllLegTrailersEmpty(legTrailers)
      : false;

  const loadStatusMismatch = isApiOrder && hasLoadStatusMismatch(legTrailers);

  // Fails closed: a stored flag on an order that no longer has an empty-trailer
  // leg waives nothing.
  const pendingBolEligible =
    (args.legTrailerLoadStatuses ?? legTrailers.map((r) => r.trailerLoadStatus)).some(
      (s) => s === "empty",
    );
  const pendingBol = pendingBolFlag && pendingBolEligible;

  return {
    missing: getMissingConfirmDocs({
      customer,
      tenderId,
      hasBolImage,
      hasTenderImage,
      allLegTrailersEmpty,
      loadStatusMismatch,
      pendingBol,
    }),
    customerName: customer?.organization_name ?? null,
    pendingBolEligible,
    pendingBolFlag,
  };
}


interface ResolvedLegTrailer {
  /** Trailer *number* (text) resolved for this leg, or null when unassigned. */
  trailerNumber: string | null;
  /** Planned load status from `legs.is_loaded` (null when unset). */
  isLoaded: boolean | null;
  /** Physical `trailers.load_status`, when the trailer resolves. */
  trailerLoadStatus: string | null;
}

/**
 * Resolves every leg's trailer and its physical load status, in one place, so
 * the empty-trailer BOL waiver and the API load-status cross-check can never
 * read different sources.
 *
 * `legs.trailer_id` is authoritative per leg; the legacy `orders.trailer_id`
 * mirror may fill a missing *first*-leg trailer only — it mirrors leg 1 and
 * would go stale for later legs. Both columns store the trailer *number*
 * (text), not `trailers.id`.
 */
async function resolveLegTrailers(
  orderId: string,
  tenantId: string | null,
): Promise<ResolvedLegTrailer[]> {
  let legsQuery = supabase
    .from("legs")
    .select("trailer_id, leg_sequence, is_loaded")
    .eq("order_id", orderId);
  if (tenantId) legsQuery = legsQuery.eq("tenant_id", tenantId);
  const { data: legRows, error: legError } = await legsQuery;
  if (legError) throw legError;

  const legs = (legRows ?? []) as {
    trailer_id: string | null;
    leg_sequence: number;
    is_loaded: boolean | null;
  }[];
  let orderTrailerNumber: string | null = null;

  if (legs.length === 0 || legs.some((leg) => leg.leg_sequence === 1 && !leg.trailer_id)) {
    let orderQuery = supabase
      .from("orders")
      .select("trailer_id")
      .eq("id", orderId);
    if (tenantId) orderQuery = orderQuery.eq("tenant_id", tenantId);
    const { data: orderRow, error: orderError } = await orderQuery.maybeSingle();
    if (orderError) throw orderError;
    orderTrailerNumber = (orderRow as { trailer_id: string | null } | null)?.trailer_id ?? null;
  }

  const resolved: ResolvedLegTrailer[] =
    legs.length === 0
      ? [{ trailerNumber: orderTrailerNumber, isLoaded: null, trailerLoadStatus: null }]
      : legs.map((leg) => ({
          trailerNumber:
            leg.trailer_id ?? (leg.leg_sequence === 1 ? orderTrailerNumber : null),
          isLoaded: leg.is_loaded ?? null,
          trailerLoadStatus: null,
        }));

  const unique = Array.from(
    new Set(resolved.map((r) => r.trailerNumber).filter((t): t is string => !!t)),
  );
  if (unique.length === 0) return resolved;

  let q = supabase.from("trailers").select("trailer_id, load_status").in("trailer_id", unique);
  if (tenantId) q = q.eq("tenant_id", tenantId);
  const { data: trailers, error: trailerError } = await q;
  if (trailerError) throw trailerError;

  const statuses = new Map<string, string | null>();
  for (const row of (trailers ?? []) as { trailer_id: string; load_status: string | null }[]) {
    statuses.set(row.trailer_id, row.load_status);
  }
  return resolved.map((r) => ({
    ...r,
    trailerLoadStatus: r.trailerNumber ? statuses.get(r.trailerNumber) ?? null : null,
  }));
}

/**
 * True only when the order has at least one resolvable trailer and every one of
 * them is `empty`. Fails closed: any leg without a resolvable empty trailer
 * keeps the BOL required.
 */
function areAllLegTrailersEmpty(resolved: readonly ResolvedLegTrailer[]): boolean {
  if (resolved.length === 0) return false;
  return resolved.every((r) => !!r.trailerNumber && r.trailerLoadStatus === "empty");
}


/**
 * Confirm-time documentation gate for one order.
 */
export function useConfirmDocGate(args: ConfirmDocGateArgs) {
  const { tenantId, tenantReady } = useTenant();
  const { isAdmin } = useUserRole();

  const { data, isLoading } = useQuery({
    queryKey: [
      "confirm-doc-gate",
      tenantId,
      args.orderId ?? null,
      args.billingPartyId ?? null,
      args.tenderId ?? null,
      args.pendingBol ?? null,
    ],
    enabled: tenantReady && (!!args.billingPartyId || !!args.customer || !!args.orderId),
    queryFn: () =>
      fetchMissingConfirmDocs({
        orderId: args.orderId,
        billingPartyId: args.billingPartyId,
        tenderId: args.tenderId,
        tenantId,
        customer: args.customer,
        pendingBol: args.pendingBol,
      }),
  });

  const missing = data?.missing ?? [];
  const blocked = missing.length > 0;
  return {
    missing,
    blocked,
    /** Billing party name, for tooltips and the override audit record. */
    customerName: data?.customerName ?? null,
    /** Admins may proceed after acknowledging a warning. */
    canOverride: isAdmin,
    /** Blocked with no way forward (non-admins). */
    hardBlocked: blocked && !isAdmin,
    /** At least one leg's trailer is physically empty — "Pending BOL" may show. */
    pendingBolEligible: data?.pendingBolEligible ?? false,
    message: formatMissingDocs(missing, data?.customerName),
    isLoading,
  };
}
